import React, { useEffect, useState } from "react";
import { NavLink } from "react-router-dom";
import { useSeo } from "../useSeo";
import EBOOKS from "../../data/ebooks.json";
import { TRAILS_DATA, ensureCuratedTrailsLoaded } from "../../data/TrailsData";

// Slug público de uma trilha: editorialSlug (ex.: "deus-como-plataforma") é
// mais descritivo pra SEO/URL que o id curto interno ("plataforma") -- mesma
// prioridade usada em PrototypeApp.js (trailUrlSlug) e em
// scripts/prerender-seo.mjs, pra não divergir da URL real da página.
function trailUrlSlug(trail) {
  return (trail && (trail.editorialSlug || trail.slug || trail.id)) || "";
}

const PAGE_TITLE = "Devocionais e Ebooks Gratuitos | Igreja Batista Mosaico";
const PAGE_DESCRIPTION = "Trilhas devocionais de alguns dias e ebooks completos, gratuitos, sobre fé, família, identidade, oração e propósito. Leia online dentro do app Família Mosaico.";

// Catálogo público de /familia/ebooks/* e /familia/trilhas/* (ver
// EbookLanding.jsx e TrailLanding.jsx -- ambas são páginas públicas,
// indexáveis, sem exigir login). Até agora essas páginas só eram
// alcançáveis por link direto (sitemap.xml e nada mais) -- nenhuma página
// pública do site linkava pra elas, o que faz o Google tratar como
// conteúdo "órfão" e despriorizar a indexação mesmo com sitemap. Esta
// página existe pra ser esse ponto de entrada: um hub real, linkado no
// rodapé do site, com um link de verdade pra cada ebook e trilha.
function useCatalogSchema(ebooks, trails) {
  useEffect(() => {
    const items = [
      ...ebooks.map((ebook, index) => ({
        "@type": "ListItem",
        position: index + 1,
        url: `https://www.batistamosaico.com.br/familia/ebooks/${ebook.id}`,
        name: ebook.title
      })),
      ...trails.map((trail, index) => ({
        "@type": "ListItem",
        position: ebooks.length + index + 1,
        url: `https://www.batistamosaico.com.br/familia/trilhas/${trailUrlSlug(trail)}`,
        name: trail.title
      }))
    ];
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "devocionais-schema";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      name: PAGE_TITLE,
      description: PAGE_DESCRIPTION,
      hasPart: { "@type": "ItemList", itemListElement: items }
    });
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, [ebooks, trails]);
}

export default function Devocionais() {
  useSeo({
    title: PAGE_TITLE,
    description: PAGE_DESCRIPTION,
    path: "/devocionais"
  });

  // As 6 trilhas escritas à mão (+ CASA 30 dias) já ficam disponíveis de
  // cara; as ~54 trilhas curadas vêm de um chunk carregado sob demanda (ver
  // comentário em TrailsData.js -- ensureCuratedTrailsLoaded), pra não
  // engordar o bundle de quem só visita as páginas institucionais. Aqui a
  // gente busca esse chunk assim que a página monta (é justamente o
  // catálogo completo que essa página promete) e re-renderiza quando
  // resolver.
  const [, forceRerender] = useState(0);
  useEffect(() => {
    let alive = true;
    ensureCuratedTrailsLoaded().then(() => {
      if (alive) forceRerender((n) => n + 1);
    });
    return () => {
      alive = false;
    };
  }, []);

  const trails = TRAILS_DATA;
  useCatalogSchema(EBOOKS, trails);

  return (
    <>
      <div className="section-head" style={{ marginTop: 34 }}>
        <p className="eyebrow">Conteúdo gratuito</p>
        <h1>Devocionais e ebooks</h1>
        <p>Trilhas de alguns dias e ebooks completos pra ler no seu tempo — sobre fé, família, identidade, oração e propósito. Tudo gratuito, dentro do app Família Mosaico.</p>
      </div>

      <div className="section-head">
        <h2>Ebooks</h2>
        <p>{EBOOKS.length} ebooks completos, escritos pelo Pr. Rodrigo Moraes.</p>
      </div>
      <div className="grid grid-2" style={{ marginBottom: 36 }}>
        {EBOOKS.map((ebook) => (
          <NavLink
            key={ebook.id}
            to={`/familia/ebooks/${ebook.id}`}
            className="card"
            style={{ textDecoration: "none", color: "inherit", display: "block" }}
          >
            <h3 style={{ margin: "0 0 6px" }}>{ebook.title}</h3>
            <p style={{ margin: "0 0 8px" }}>{ebook.subtitle}</p>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
              {ebook.author}
              {ebook.pages ? ` · ${ebook.pages} páginas` : ""}
            </p>
          </NavLink>
        ))}
      </div>

      <div className="section-head">
        <h2>Trilhas devocionais</h2>
        <p>{trails.length} trilhas, de {Math.min(...trails.map((t) => t.duration || 5))} a {Math.max(...trails.map((t) => t.duration || 5))} dias cada.</p>
      </div>
      <div className="grid grid-2" style={{ marginBottom: 36 }}>
        {trails.map((trail) => (
          <NavLink
            key={trail.id}
            to={`/familia/trilhas/${trailUrlSlug(trail)}`}
            className="card"
            style={{ textDecoration: "none", color: "inherit", display: "block" }}
          >
            <h3 style={{ margin: "0 0 6px" }}>{trail.title}</h3>
            <p style={{ margin: "0 0 8px" }}>{trail.subtitle}</p>
            <p style={{ margin: 0, fontSize: 12, opacity: 0.7 }}>
              {trail.duration ? `${trail.duration} dia${trail.duration === 1 ? "" : "s"}` : ""}
            </p>
          </NavLink>
        ))}
      </div>

      <div className="card" style={{ textAlign: "center" }}>
        <h3 style={{ marginTop: 0 }}>Quer acompanhar sua leitura?</h3>
        <p>Crie uma conta gratuita no app Família Mosaico pra salvar seu progresso em qualquer ebook ou trilha.</p>
        <NavLink className="cta-pill" to="/entrar?next=%2Ffamilia">Criar conta grátis</NavLink>
      </div>
    </>
  );
}
